import React from 'react';
import {
  View,
  Text,
  Image,
  StyleSheet,
  TouchableOpacity,
} from 'react-native';
import {useTheme} from '../hooks/useTheme';
import {Icon} from './Icon';
import {Article} from '../types/article';

interface ArticleCardProps {
  article: Article;
  onPress: () => void;
  featured?: boolean;
}

export const ArticleCard: React.FC<ArticleCardProps> = ({article, onPress, featured = false}) => {
  const {colors} = useTheme();

  const dynamicStyles = {
    container: {
      backgroundColor: colors.cardBackground,
      shadowColor: colors.textPrimary,
    },
    categoryBadge: {
      backgroundColor: colors.primaryPastel,
    },
    categoryText: {
      color: colors.primary,
    },
    title: {
      color: colors.textPrimary,
    },
    author: {
      color: colors.textSecondary,
    },
    comments: {
      color: colors.textSecondary,
    },
    date: {
      color: colors.primary,
    },
  };

  // Featured layout - big image on top
  if (featured) {
    return (
      <TouchableOpacity 
        style={[styles.featuredContainer, dynamicStyles.container]} 
        onPress={onPress} 
        activeOpacity={0.7}
      >
        {/* Big Image */}
        <Image
          source={{uri: article.image || 'https://via.placeholder.com/400x200'}}
          style={styles.featuredImage}
          resizeMode="cover"
        />
        
        {/* Live indicator */}
        <View style={styles.liveIndicator}>
          <View style={styles.liveDot} />
          <Text style={styles.liveText}>LIVE</Text>
        </View>

        {/* Content below image */}
        <View style={styles.featuredContent}>
          {/* Category Badge */}
          {article.category && (
            <View style={[styles.categoryBadge, dynamicStyles.categoryBadge]}>
              <Text style={[styles.categoryText, dynamicStyles.categoryText]}>
                {article.category.toUpperCase()}
              </Text>
            </View>
          )}

          {/* Title - larger for featured */}
          <Text style={[styles.featuredTitle, dynamicStyles.title]} numberOfLines={3}>
            {article.name}
          </Text>

          {/* Footer */}
          <View style={styles.footer}>
            <Text style={[styles.author, dynamicStyles.author]} numberOfLines={1}>
              {article.author || 'Redazione'}
            </Text>
            <View style={styles.rightInfo}>
              {article.comments_count !== undefined && (
                <View style={styles.commentsContainer}>
                  <Icon name="comment" size={10} color={colors.textSecondary} />
                  <Text style={[styles.comments, dynamicStyles.comments]}>{article.comments_count}</Text>
                </View>
              )}
              <Text style={[styles.date, dynamicStyles.date]}>{article.formatted_date}</Text>
            </View>
          </View>
        </View>
      </TouchableOpacity>
    );
  }

  // Regular layout
  return (
    <TouchableOpacity 
      style={[styles.container, dynamicStyles.container]} 
      onPress={onPress} 
      activeOpacity={0.7}
    >
      {/* Image - fixed size square */}
      <Image
        source={{uri: article.image || 'https://via.placeholder.com/90'}}
        style={styles.image}
        resizeMode="cover"
      />

      {/* Content - flex to fill remaining space */}
      <View style={styles.content}>
        {/* Category Badge - pastel purple */}
        {article.category && (
          <View style={[styles.categoryBadge, dynamicStyles.categoryBadge]}>
            <Text style={[styles.categoryText, dynamicStyles.categoryText]}>
              {article.category.toUpperCase()}
            </Text>
          </View>
        )}

        {/* Title */}
        <View style={styles.titleWrapper}>
          <Text style={[styles.title, dynamicStyles.title]} numberOfLines={3}>
            {article.name}
          </Text>
        </View>

        {/* Footer - Author left, Comments/Date right */}
        <View style={styles.footer}>
          <Text style={[styles.author, dynamicStyles.author]} numberOfLines={1}>
            {article.author || 'Redazione'}
          </Text>
          <View style={styles.rightInfo}>
            {article.comments_count !== undefined && (
              <View style={styles.commentsContainer}>
                <Icon name="comment" size={10} color={colors.textSecondary} />
                <Text style={[styles.comments, dynamicStyles.comments]}>{article.comments_count}</Text>
              </View>
            )}
            <Text style={[styles.date, dynamicStyles.date]}>{article.formatted_date}</Text>
          </View>
        </View>
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    borderRadius: 8,
    marginHorizontal: 12,
    marginVertical: 6,
    height: 110,
    shadowOffset: {width: 0, height: 1},
    shadowOpacity: 0.08,
    shadowRadius: 3,
    elevation: 2,
    overflow: 'hidden',
  },
  // Featured layout styles
  featuredContainer: {
    borderRadius: 12,
    marginHorizontal: 12,
    marginVertical: 8,
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.12,
    shadowRadius: 6,
    elevation: 4,
    overflow: 'hidden',
  },
  featuredImage: {
    width: '100%',
    height: 200,
  },
  featuredContent: {
    padding: 14,
  },
  featuredTitle: {
    fontSize: 18,
    fontWeight: '700',
    lineHeight: 24,
    marginBottom: 8,
  },
  liveIndicator: {
    position: 'absolute',
    top: 12,
    left: 12,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.7)',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 4,
    gap: 6,
  },
  liveDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#ff0000',
  },
  liveText: {
    color: '#ffffff',
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  image: {
    width: 110,
    height: '100%',
  },
  content: {
    flex: 1,
    padding: 10,
    justifyContent: 'space-between',
  },
  categoryBadge: {
    alignSelf: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
    marginBottom: 4,
  },
  categoryText: {
    fontSize: 9,
    fontWeight: '600',
    letterSpacing: 0.3,
  },
  titleWrapper: {
    flex: 1,
    justifyContent: 'center',
  },
  title: {
    fontSize: 13,
    fontWeight: '600',
    lineHeight: 17,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 4,
  },
  author: {
    fontSize: 11,
    flex: 1,
    marginRight: 8,
  },
  rightInfo: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  commentsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 8,
    gap: 3,
  },
  comments: {
    fontSize: 10,
  },
  date: {
    fontSize: 10,
    fontWeight: '500',
  },
});
